import React, {useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from "@mui/material/TextField";
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import FormControl from "@mui/material/FormControl";
import * as yup from "yup";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {httpsCallable} from "firebase/functions";
import {getFirebase} from "../../config/firebase.config";
import LoadingBackdrop from "../common/LoadingBackdrop";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";


const schema = yup.object({
    email: yup.string().email("Invalid email address!").required("Email is required!"),
    empId: yup.string().required("Employee Id required!"),
    empName: yup.string().required("Employee name required!"),
    empPhone: yup.string(),
    empAddress: yup.string(),
}).required();

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: {
        xs: 300,
        sm: 400,
        md: 500,
    },
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const EmployeeForm = ({open, handleClose, initialValues}) => {
    const {functions} = getFirebase();
    const {handleSubmit, control, formState: {errors}, reset} = useForm({
        resolver: yupResolver(schema)
    });
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        reset(initialValues)
        setMessage(null);
        setError(null);
    }, [initialValues, reset]);

    const handleFormSubmit = async (data) => {
        const addEmployee = httpsCallable(functions, "addEmployee");
        setLoading(true);
        addEmployee(data).then(result => {
            const responseData = result.data;
            const resMessage = responseData.message;
            if (resMessage) {
                setMessage(resMessage);
            }
            const errorInfo = responseData.errorInfo;
            if (errorInfo) {
                setError(errorInfo)
            }
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            reset(initialValues);
            setLoading(false);
        });
    }
    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={style}>
                <LoadingBackdrop open={loading}/>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    Employee Form
                </Typography>
                <Box component="form" noValidate sx={{mt: 1}} onSubmit={handleSubmit(handleFormSubmit)}>
                    <FormControl fullWidth={true}>
                        <Controller
                            name="email"
                            control={control}
                            render={({field}) =>
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    label="Employee Email Address"
                                    autoComplete="email"
                                    autoFocus
                                    error={!!errors.email}
                                    helperText={errors.email && errors.email?.message}
                                />}
                        />
                    </FormControl>
                    <FormControl fullWidth={true}>
                        <Controller
                            name="empId"
                            control={control}
                            render={({field}) =>
                                <TextField
                                    {...field}
                                    error={!!errors.empId}
                                    helperText={errors.empId && errors.empId?.message}
                                    margin="normal"
                                    fullWidth
                                    label="Employee ID"
                                    autoComplete="empId"
                                />}
                        />
                    </FormControl>
                    <FormControl fullWidth={true}>
                        <Controller
                            name="empName"
                            control={control}
                            render={({field}) =>
                                <TextField
                                    {...field}
                                    error={!!errors.empName}
                                    helperText={errors.empName && errors.empName?.message}
                                    margin="normal"
                                    fullWidth
                                    label="Employee name"
                                    autoComplete="empName"
                                />}
                        />
                    </FormControl>
                    <FormControl fullWidth={true}>
                        <Controller
                            name="empPhone"
                            control={control}
                            render={({field}) =>
                                <TextField
                                    {...field}
                                    error={!!errors.empPhone}
                                    helperText={errors.empPhone && errors.empPhone?.message}
                                    margin="normal"
                                    fullWidth
                                    label="Employee phone number"
                                    autoComplete="empPhone"
                                />}
                        />
                    </FormControl>
                    <FormControl fullWidth={true}>
                        <Controller
                            name="empAddress"
                            control={control}
                            render={({field}) =>
                                <TextField
                                    {...field}
                                    error={!!errors.empAddress}
                                    helperText={errors.empAddress && errors.empAddress?.message}
                                    margin="normal"
                                    fullWidth
                                    label="Employee address"
                                    autoComplete="empAddress"
                                />}
                        />
                    </FormControl>
                    <Button
                        type="submit"

                        variant="contained"
                        sx={{mt: 1}}
                    >
                        Submit
                    </Button>
                    <Button
                        type="button"
                        variant="outlined"
                        color="error"
                        sx={{mt: 1, ml: 1}}
                    >
                        Delete
                    </Button>
                </Box>
                <Stack sx={{width: '100%', mt: 2}} spacing={2}>
                    {error && <Alert severity="error">{error.message}</Alert>}
                    {message && <Alert severity="success">{message}</Alert>}
                </Stack>
            </Box>
        </Modal>
    );
};

export default EmployeeForm;